import { useLoaderData } from '@remix-run/react';
import type { loader } from '~/routes/_index.tsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Feed, FeedEntry, UIComponent } from '~/services/layout/index.ts';
import { HorizontalListButton } from './button.tsx';
import {
  useDispachResizeEvent,
  useHorizontalListAutoScroll,
  useHorizontalListButtonPosition,
  useHorizontalListResize,
  useHorizontalListScroll,
  useUpdateIndicatorsIndexOnScroll,
  useHorizontalListSnap,
  useHorizontalListCircularScrollReset,
  useInitialScrollToFirstDuplicate,
} from './hooks.ts';
import { useNextFeedPagination } from '~/hooks/use-next-feed.ts';
import { PowerCell } from '~/plugins/power-cell/components/index.tsx';
import HorizontalListIndicators from './indicators.tsx';
import manifest from '../config/manifest.ts';
import { getPluginConfig } from '~/utils/get-plugin-configuration.ts';
import { cn } from '~/components2/utils.ts';
import {
  getHorizontalListButtonsVisibility,
  getHorizontalListPadding,
  getHorizontalListMaxWidthClasses,
  getHorizontalListScrollSnapClasses,
  getIndicatorsEntries,
  getIsAllCellsInView,
  getOffScreenOpacityLoadingClasses,
  getHorizontalListMaxWidthRightPadding,
  getHorizontalListScrollPx,
  getScrollingStrategy,
} from './getters.ts';
import { getTranslations } from '~/services/localization/index.ts';
import { useFeedEntries } from '~/hooks/use-feed-entries.ts';
import { useCircularFeedEntries } from '~/hooks/useCircularFeedEntries.ts';
import { useHorizontalListResponsiveValues } from './use-horizontal-list-responsive-values.ts';
import { triggerScrollAction } from './trigger-scroll-action.ts';
import { useOffScreenOpacity } from './use-off-screen-opacity.ts';
import { getUniqueKeyByEntry } from '~/services/layout/layout.ts';
import { useResponsiveValue } from '~/hooks/use-responsive-breakpoint.ts';
import { usePreferenceEditorPayload } from '~/hooks/preference-selector/use-preference-editor.ts';
import type { StorageFeedsData } from '~/components/ui-component.tsx';
import type { UseIsLoggedInReturnType } from '~/hooks/use-is-logged-in.ts';
import { getLimit } from '~/services/layout/layout.ts';
import { getIndicatorProps } from './utils.ts';

const ANIMATION_TIMEOUT = 800;
const isClient = typeof window !== 'undefined';

/**
 * DEBUG_HORIZONTAL_LIST_SWITCH flag can be toggled to enable detailed console logs
 * for debugging the HorizontalListV2 component.
 * When enabled, it will log key actions, state changes, and configuration values with
 * the component ID for easy identification in the console.
 */
const DEBUG_HORIZONTAL_LIST_SWITCH = false;
const DEBUG_HORIZONTAL_LIST_COMPONENT_ID = '';

export default function HorizontalListV2({
  uiComponent,
  feed,
  isLoading,
  isClientStorage,
  storageFeedsData,
  isLoggedInData,
}: {
  uiComponent: UIComponent;
  feed?: Feed;
  isLoading?: boolean;
  isClient?: boolean;
  isClientStorage?: boolean;
  storageFeedsData: StorageFeedsData;
  isLoggedInData: UseIsLoggedInReturnType;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const cellContainerRef = useRef<HTMLDivElement>(null);
  const cellChildContainerRefs = useRef<any>([]);
  const cellRef = useRef<HTMLAnchorElement>(null);

  const { styles, rules } = getPluginConfig<typeof manifest>(
    manifest,
    uiComponent
  );
  const itemLimit: number | undefined = rules?.item_limit;

  const baseFeedEntries: FeedEntry[] = useMemo(
    () => feed?.entry?.slice(0, getLimit(itemLimit)) || [],
    [feed?.entry, itemLimit]
  );

  const isAllCellsInView = getIsAllCellsInView({
    styles,
    entriesCount: baseFeedEntries?.length || 0,
  });

  const isAllCellsInViewResponsive = useResponsiveValue(
    isAllCellsInView,
    false,
    false
  );

  const hideButtonsBasedOnCellsInView = useResponsiveValue(
    isAllCellsInView,
    true,
    true
  );

  const { indicators_switch, arrow_position } = styles;

  const { defaultThemeV2Config } = useLoaderData<typeof loader>();

  const {
    gutter,
    paddingLeft,
    nextCellOffset,
    autoscrollSwitch,
    autoscrollInterval,
    loopSwitch,
    offScreenOpacitySwitch,
    arrowsSwitch,
    cellScalingSwitch,
    indicatorsAlignment,
    isOneCellPerView,
    cellsPerView,
    componentMaxWidth,
    fixedCellWidth,
  } = useHorizontalListResponsiveValues({
    rules,
    styles,
    defaultThemeV2Config,
  });

  // Calculate circular condition with responsive values
  const circularCondition: boolean = isAllCellsInViewResponsive
    ? false
    : rules?.circular_scrolling_switch || (autoscrollSwitch && loopSwitch);

  const [nextFeedUrl, setNextFeedUrl] = useState<string | undefined>(undefined);

  // Manage circular scrolling entries lifecycle
  // Pagination takes precedence - circular is disabled when nextFeedUrl exists
  const {
    feedEntries,
    setFeedEntries,
    originalFeedEntriesLength,
    shouldEnableCircular,
  } = useCircularFeedEntries({
    baseFeedEntries,
    circularCondition,
    isLoading,
    nextFeedUrl,
  });

  const [toggleButtons, setToggleButtons] = useState({
    enabled: false,
    left: false,
    right: false,
    now: new Date().getTime(),
  });

  const [isNextFeedLoading, setIsNextFeedLoading] = useState(false);
  const [currentIndicatorsIndex, setCurrentIndicatorsIndex] =
    useState<number>(0);
  const currentIndicatorsIndexRef = useRef<number>(0);

  // Keep ref updated with state value
  currentIndicatorsIndexRef.current = currentIndicatorsIndex;

  const [isScrolling, setIsScrolling] = useState(false);
  const [isScrollingByClick, setIsScrollingByClick] = useState<boolean>(false);
  const [isScrollingByIndicatorClick, setIsScrollingByIndicatorClick] =
    useState<boolean>(false);
  const scrollByClickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollByIndicatorClickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  usePreferenceEditorPayload({
    feed,
    uiComponent,
    setFeedEntries,
  });

  const entries = useFeedEntries({
    feedEntries,
    setFeedEntries,
    isLoading,
    isClient,
    feed,
    itemLimit,
    uiComponent,
  });

  const { showLeftButton, showRightButton } =
    getHorizontalListButtonsVisibility({
      ref,
      cellRef,
      feedEntriesLength: entries.length,
      circularCondition: shouldEnableCircular,
      isNextFeedLoading,
      gutter,
      isOneCellPerView,
      componentMaxWidth,
    });

  const horizontalListButtonPosition = useHorizontalListButtonPosition({
    isNextFeedLoading,
    arrow_position,
    ref,
    cellRef,
    position: arrow_position,
  });

  const updateScrollingByClickState = useCallback(() => {
    setIsScrollingByClick(false);
    setIsScrollingByIndicatorClick(false);
  }, []);

  const scheduleScrollByClickReset = useCallback(() => {
    if (scrollByClickTimeoutRef.current) {
      clearTimeout(scrollByClickTimeoutRef.current);
    }
    scrollByClickTimeoutRef.current = setTimeout(() => {
      setIsScrollingByClick(false);
      scrollByClickTimeoutRef.current = null;
    }, 500);
  }, []);

  const scheduleScrollByIndicatorClickReset = useCallback(() => {
    if (scrollByIndicatorClickTimeoutRef.current) {
      clearTimeout(scrollByIndicatorClickTimeoutRef.current);
    }
    scrollByIndicatorClickTimeoutRef.current = setTimeout(() => {
      setIsScrollingByIndicatorClick(false);
      scrollByIndicatorClickTimeoutRef.current = null;
    }, 500);
  }, []);

  const scrollingStrategy = getScrollingStrategy({
    screenWidth: ref?.current?.clientWidth ?? 0,
    itemsPerView: cellsPerView,
    fixedItemSize: fixedCellWidth,
  });

  const feedEntriesLength: number = feedEntries?.length || 0;

  const shouldDebugHorizontalList: boolean =
    DEBUG_HORIZONTAL_LIST_SWITCH &&
    uiComponent?.id === DEBUG_HORIZONTAL_LIST_COMPONENT_ID;

  const logHorizontalListDebug = useCallback(
    (label: string, payload: Record<string, unknown>) => {
      if (!shouldDebugHorizontalList) return;

      console.log(`~ HorizontalListV2 ${label}`, {
        uiComponentId: uiComponent?.id,
        ...payload,
      });
    },
    [shouldDebugHorizontalList, uiComponent?.id]
  );

  const isInitialPositioning = useInitialScrollToFirstDuplicate({
    scrollRef: ref,
    cellRef,
    circularCondition: shouldEnableCircular,
    originalEntriesLength: originalFeedEntriesLength,
    gutter,
    nextCellOffset,
    enabled:
      !!ref?.current?.clientWidth && scrollingStrategy === 'scrollByPage',
  });

  useHorizontalListScroll({
    ref,
    circularCondition: shouldEnableCircular,
    setToggleButtons,
    nextFeedUrl,
    isScrolling,
    setIsScrolling,
    setIsScrollingByClick: updateScrollingByClickState,
    gutter,
    componentMaxWidth,
    isInitialPositioning,
  });

  useNextFeedPagination({
    feed,
    nextFeedUrl,
    setNextFeedUrl,
    setIsNextFeedLoading,
    isNextFeedLoading,
    setFeedEntries,
    feedEntries,
    itemLimit,
    condition:
      !!nextFeedUrl &&
      toggleButtons.enabled &&
      !showRightButton &&
      !isLoading &&
      !shouldEnableCircular &&
      !indicators_switch,
    isLoading,
  });

  useHorizontalListResize({
    entries,
    ref,
    cellRef,
    nextFeedUrl,
    circularCondition: shouldEnableCircular,
    setToggleButtons,
    isLoading,
    gutter,
    componentMaxWidth,
  });

  useDispachResizeEvent({
    ref,
    circularCondition: shouldEnableCircular,
    nextFeedUrl,
    gutter,
    componentMaxWidth,
  });

  useHorizontalListSnap({
    ref,
    isScrolling,
    isOneCellPerView,
  });

  const scrollLeftOffset: number = getHorizontalListScrollPx({
    gutter,
    cellsPerView,
    nextCellOffset,
    paddingLeft,
    componentMaxWidth,
    ref,
  });

  // For debugging purposes only
  const useIndexScrollForSingleCellNoSpacing: boolean =
    isOneCellPerView &&
    !shouldEnableCircular &&
    gutter === 0 &&
    paddingLeft === 0 &&
    nextCellOffset === 0 &&
    componentMaxWidth === 0;

  const useIndexScrollForSingleCellPageMode: boolean =
    isOneCellPerView &&
    !shouldEnableCircular &&
    scrollingStrategy === 'scrollByPage';

  useEffect(() => {
    if (!shouldDebugHorizontalList) return;

    console.log('~ HorizontalListV2 config', {
      uiComponentId: uiComponent?.id,
      isOneCellPerView,
      shouldEnableCircular,
      gutter,
      paddingLeft,
      nextCellOffset,
      componentMaxWidth,
      cellsPerView,
      fixedCellWidth,
      scrollingStrategy,
      scrollLeftOffset,
      useIndexScrollForSingleCellNoSpacing,
      useIndexScrollForSingleCellPageMode,
    });
  }, [
    cellsPerView,
    componentMaxWidth,
    fixedCellWidth,
    gutter,
    isOneCellPerView,
    nextCellOffset,
    paddingLeft,
    scrollLeftOffset,
    scrollingStrategy,
    shouldDebugHorizontalList,
    shouldEnableCircular,
    uiComponent?.id,
    useIndexScrollForSingleCellPageMode,
    useIndexScrollForSingleCellNoSpacing,
  ]);

  /**
   * Scrolls to a specific index in the horizontal list.
   * Supports both arrow-based scrolling (by offset) and indicator-based scrolling (by item).
   * @param index - The target index to scroll to.
   * @param direction - Optional direction for arrow-based scrolling ('left' or 'right').
   * @param hasOverScrolled - Flag indicating if scrolling beyond real entries (for circular behavior alignment).
   */
  const scrollToIndex = useCallback(
    (
      index: number,
      direction?: 'left' | 'right' | undefined,
      hasOverScrolled?: boolean
    ) => {
      if (cellContainerRef?.current) {
        cellContainerRef.current.dispatchEvent(new Event('scroll'));

        if (direction) {
          if (!ref.current) return;

          logHorizontalListDebug('scrollToIndex-offset', {
            index,
            direction,
            hasOverScrolled,
            currentScrollLeft: ref.current.scrollLeft,
            scrollLeftOffset,
            containerClientWidth: ref.current.clientWidth,
            containerScrollWidth: ref.current.scrollWidth,
          });

          ref.current.scrollBy({
            left: direction === 'left' ? -scrollLeftOffset : scrollLeftOffset,
            behavior: 'smooth',
          });
        } else {
          const target = cellContainerRef.current?.children[index];

          if (!target || !ref.current) return;

          ref.current.style.scrollSnapType = 'x mandatory';

          // Calculate horizontal scroll position without affecting vertical scroll
          const containerRect = ref.current.getBoundingClientRect();
          const targetRect = (target as HTMLElement).getBoundingClientRect();
          const currentScrollLeft = ref.current.scrollLeft;

          let targetScrollLeft: number;

          if (hasOverScrolled) {
            // Align to start
            targetScrollLeft =
              currentScrollLeft + (targetRect.left - containerRect.left);
          } else {
            // Align to center
            const containerCenter = containerRect.width / 2;
            const targetCenter = targetRect.width / 2;
            targetScrollLeft =
              currentScrollLeft +
              (targetRect.left - containerRect.left) -
              containerCenter +
              targetCenter;
          }

          logHorizontalListDebug('scrollToIndex-index', {
            index,
            direction,
            hasOverScrolled,
            currentScrollLeft,
            targetScrollLeft,
            containerLeft: containerRect.left,
            containerWidth: containerRect.width,
            targetLeft: targetRect.left,
            targetWidth: targetRect.width,
          });

          ref.current.scrollTo({
            left: targetScrollLeft,
            behavior: 'smooth',
          });

          ref.current.style.scrollSnapType = 'none';
        }
      }
    },
    [scrollLeftOffset]
  );

  /**
   * Handles scroll action for arrow buttons in both directions.
   * Scrolls to the previous/next item and triggers scroll events for autoscroll pause.
   * @param direction - The direction to scroll ('left' or 'right').
   */
  const scrollAction = useCallback(
    (direction: 'left' | 'right') => {
      if (!ref.current) return;

      ref.current.style.scrollSnapType = 'x mandatory';

      const newIndex =
        direction === 'left'
          ? Math.max(0, currentIndicatorsIndexRef.current - 1)
          : currentIndicatorsIndexRef.current + 1;

      logHorizontalListDebug('scrollAction-click', {
        direction,
        currentIndicatorsIndex: currentIndicatorsIndexRef.current,
        newIndex,
        currentScrollLeft: ref.current.scrollLeft,
        scrollLeftOffset,
        useIndexScrollForSingleCellNoSpacing,
        useIndexScrollForSingleCellPageMode,
      });

      if (useIndexScrollForSingleCellPageMode) {
        scrollToIndex(newIndex);
      } else {
        scrollToIndex(newIndex, direction);
      }
      setIsScrollingByClick(true);
      scheduleScrollByClickReset();

      ref.current.style.scrollSnapType = 'none';
    },
    [
      scheduleScrollByClickReset,
      scrollToIndex,
      useIndexScrollForSingleCellNoSpacing,
      useIndexScrollForSingleCellPageMode,
    ]
  );

  /**
   * Performs instant circular scroll reset when reaching duplicate entries.
   * When scrolling right past duplicates, jumps back to equivalent position in originals.
   * When scrolling left past originals, jumps forward to equivalent position in duplicates.
   * @param direction - The direction of the circular reset ('left' or 'right').
   */
  const performCircularReset = useCallback(
    (direction: 'left' | 'right') => {
      if (!ref.current || !cellContainerRef.current) return;

      const segmentWidth = ref.current.scrollWidth / 2;
      const currentScrollLeft = ref.current.scrollLeft;
      const maxScrollLeft = ref.current.scrollWidth - ref.current.clientWidth;

      let targetScrollLeft: number;

      if (direction === 'right') {
        // Scrolling right past duplicates: jump back to equivalent position in originals
        targetScrollLeft = currentScrollLeft - segmentWidth;
      } else {
        // Scrolling left past originals: jump forward to equivalent position in duplicates
        targetScrollLeft = currentScrollLeft + segmentWidth;
      }

      // Adjust for paddingLeft to align correctly with the start
      // if (targetScrollLeft < paddingLeft) targetScrollLeft = 0;

      // Ensure target position stays within valid scroll bounds
      targetScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));

      ref.current.scrollTo({
        left: targetScrollLeft,
        behavior: 'instant',
      });
    },
    [paddingLeft]
  );

  useHorizontalListCircularScrollReset({
    ref,
    circularCondition: shouldEnableCircular,
    feedEntriesLength,
    originalFeedEntriesLength,
    isLoading,
    isInitialPositioning,
    paddingLeft,
  });

  const indicatorsEntries = getIndicatorsEntries({
    feed,
    itemLimit,
  });

  useUpdateIndicatorsIndexOnScroll({
    ref,
    indicators_switch,
    circularCondition: shouldEnableCircular,
    feedEntry: indicatorsEntries,
    autoscrollSwitch,
    loopSwitch,
    setCurrentIndicatorsIndex,
    isScrollingByClick: isScrollingByClick || isScrollingByIndicatorClick,
    isInitialPositioning,
  });

  const isOffScreenOpacityLoading: boolean = useOffScreenOpacity({
    ref,
    cellChildContainerRefs,
    feedEntries,
    offScreenOpacity: styles?.off_screen_opacity,
    offScreenOpacitySwitch,
    isNextFeedLoading,
    isLoading,
    componentMaxWidth,
    isScrollingByClick,
  });

  /**
   * Main scroll handler that determines scrolling behavior based on strategy.
   * Uses scrollByOne for large items (>55% screen width, no itemsPerView configured).
   * Uses scrollByPage for multiple items per view.
   * Handles circular scrolling edge cases and triggers appropriate scroll actions.
   * @param direction - The direction to scroll ('left' or 'right').
   * @returns A callback function that executes the scroll action.
   */
  const handleScroll = useCallback(
    (direction: 'left' | 'right') => () => {
      logHorizontalListDebug('handleScroll-trigger', {
        direction,
        scrollingStrategy,
        currentIndicatorsIndex: currentIndicatorsIndexRef.current,
        currentScrollLeft: ref.current?.scrollLeft,
        shouldEnableCircular,
        originalFeedEntriesLength,
        feedEntriesLength: feedEntries?.length || 0,
      });

      if (scrollingStrategy === 'scrollByOne') {
        let targetIndex =
          direction === 'left'
            ? currentIndicatorsIndexRef.current - 1
            : currentIndicatorsIndexRef.current + 1;

        const hasOverScrolledRealEntries =
          shouldEnableCircular && targetIndex >= originalFeedEntriesLength;

        if (shouldEnableCircular && hasOverScrolledRealEntries) {
          setTimeout(() => performCircularReset(direction), ANIMATION_TIMEOUT);
        } else if (shouldEnableCircular && targetIndex < 0) {
          // Jump to the end when scrolling left past the start
          targetIndex = originalFeedEntriesLength - 1;
          performCircularReset(direction);
        }

        logHorizontalListDebug('handleScroll-scrollByOne', {
          direction,
          targetIndex,
          hasOverScrolledRealEntries,
          currentIndicatorsIndex: currentIndicatorsIndexRef.current,
        });

        scrollToIndex(targetIndex, undefined, hasOverScrolledRealEntries);
        setIsScrollingByClick(true);
        scheduleScrollByClickReset();
      } else {
        logHorizontalListDebug('handleScroll-scrollByPage', {
          direction,
          currentIndicatorsIndex: currentIndicatorsIndexRef.current,
          currentScrollLeft: ref.current?.scrollLeft,
        });

        triggerScrollAction({
          ref,
          cellRef,
          gutter,
          feedEntriesLength: feedEntries?.length || 0,
          circularCondition: shouldEnableCircular,
          scrollAction,
          direction,
          componentMaxWidth,
          performCircularReset,
        });
      }
    },
    [
      shouldEnableCircular,
      componentMaxWidth,
      feedEntries?.length,
      gutter,
      scrollAction,
      scrollToIndex,
      scrollingStrategy,
      originalFeedEntriesLength,
      performCircularReset,
      scheduleScrollByClickReset,
    ]
  );

  const { startAutoscrollTimer, stopAutoscrollTimer } =
    useHorizontalListAutoScroll({
      ref,
      isLoading,
      scrollAction: handleScroll('right'),
      feedEntries,
      cellRef,
      gutter,
      autoscrollSwitch,
      autoscrollInterval,
      loopSwitch,
      componentMaxWidth,
    });

  const onIndicatorUpdate = useCallback(
    (index: number) => {
      stopAutoscrollTimer();
      setIsScrollingByIndicatorClick(true);

      scrollToIndex(index);
      setCurrentIndicatorsIndex(index);

      scheduleScrollByIndicatorClickReset();
      startAutoscrollTimer();
    },
    [
      scrollToIndex,
      startAutoscrollTimer,
      stopAutoscrollTimer,
      scheduleScrollByIndicatorClickReset,
    ]
  );

  const translations = getTranslations(uiComponent?.localizations);
  const titleOverride = translations?.title_override;

  return (
    <div
      className={`component-${uiComponent?.id} horizontal-list-parent-classes relative flex flex-shrink flex-grow flex-col`}
    >
      <div
        ref={ref}
        className={`${getHorizontalListPadding({
          styles,
          defaultThemeV2Config,
        })} flex snap-mandatory flex-row overflow-x-scroll scroll-smooth scrollbar-hide`}
        data-testid={`horizontal-list-container-${uiComponent?.id}`}
      >
        {!hideButtonsBasedOnCellsInView && arrowsSwitch && (
          <HorizontalListButton
            onClick={handleScroll('left')}
            direction="left"
            show={(showLeftButton && !isLoading) || shouldEnableCircular}
            uiComponent={uiComponent}
            isNextFeedLoading={isNextFeedLoading}
            position={horizontalListButtonPosition}
            defaultThemeV2Config={defaultThemeV2Config}
            styles={styles}
          />
        )}
        <div
          ref={cellContainerRef}
          className={`z-0 flex w-full flex-row ${getHorizontalListMaxWidthClasses(
            {
              styles,
              defaultThemeV2Config,
            }
          )}`}
          data-horizontal-list-container
        >
          {entries?.map((entry, i) => {
            const title = titleOverride || entry?.title;
            const cellEntry = { ...entry, title };

            return (
              <div
                className={cn(
                  entries.length - 1 !== i ? 'horizontal-list-gutter' : '',
                  'shrink-0 snap-both snap-always',
                  {
                    'horizontal-list-cell-width': cellScalingSwitch,
                  },
                  getHorizontalListScrollSnapClasses(styles, cellScalingSwitch),
                  isOffScreenOpacityLoading &&
                    getOffScreenOpacityLoadingClasses(styles)
                )}
                ref={(el) => (cellChildContainerRefs.current[i] = el)}
                key={getUniqueKeyByEntry('horizontal-list', entry, i)}
              >
                <PowerCell
                  cellRef={cellRef}
                  uiComponent={uiComponent}
                  entry={cellEntry}
                  selectable={uiComponent?.rules?.cells_selectable_switch}
                  cellClassNames={
                    cellScalingSwitch ? 'w-full' : 'component-cell-width'
                  }
                  isLoading={isLoading || isInitialPositioning}
                  storageFeedsData={storageFeedsData}
                  isLoggedInData={isLoggedInData}
                />
              </div>
            );
          })}
          {cellScalingSwitch && (
            <div
              className={`hl-padding-fix ${getHorizontalListMaxWidthRightPadding(
                {
                  styles,
                  defaultThemeV2Config,
                }
              )}`}
            />
          )}
        </div>
        {!hideButtonsBasedOnCellsInView && arrowsSwitch && (
          <HorizontalListButton
            onClick={handleScroll('right')}
            direction="right"
            show={
              (showRightButton && !isLoading) ||
              isNextFeedLoading ||
              shouldEnableCircular
            }
            uiComponent={uiComponent}
            isNextFeedLoading={isNextFeedLoading}
            position={horizontalListButtonPosition}
            styles={styles}
            defaultThemeV2Config={defaultThemeV2Config}
          />
        )}
      </div>
      <HorizontalListIndicators
        {...getIndicatorProps({
          indicatorsEntries,
          styles,
          isLoading,
        })}
        circularCondition={shouldEnableCircular}
        alignment={indicatorsAlignment}
        currentIndicatorsIndex={currentIndicatorsIndex}
        onIndicatorUpdate={onIndicatorUpdate}
        onLeftArrowPress={handleScroll('left')}
        onRightArrowPress={handleScroll('right')}
        uiComponentId={uiComponent?.id}
      />
    </div>
  );
}
